import { useEffect, useRef } from "react";

const features = [
  {
    icon: "ri-pulse-line",
    title: "Gestão preventiva de saúde",
    desc: "Antecipamos riscos antes que se tornem problemas. Algoritmos de IA analisam padrões e alertam o RH com meses de antecedência.",
  },
  {
    icon: "ri-robot-2-line",
    title: "Concierge humano + IA",
    desc: "A melhor combinação: empatia humana com a velocidade e precisão da inteligência artificial. Atendimento 24/7 sem perder o toque pessoal.",
  },
  {
    icon: "ri-dashboard-3-line",
    title: "Dashboard executivo em tempo real",
    desc: "Visibilidade total sobre saúde, custos e indicadores de RH. Decisões baseadas em dados, não em suposições.",
  },
  {
    icon: "ri-mental-health-line",
    title: "Gestão de saúde emocional corporativa",
    desc: "Programas estruturados de bem-estar mental, prevenção de burnout e fortalecimento da cultura organizacional.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Consultoria financeira dos benefícios",
    desc: "Otimização de custos sem redução de qualidade. Identificamos oportunidades de economia e maximizamos o ROI dos benefícios.",
  },
  {
    icon: "ri-microscope-line",
    title: "Medicina integrada + dados + prevenção",
    desc: "Saúde preventiva baseada em dados reais. Integramos medicina, tecnologia e analytics para reduzir afastamentos e sinistralidade.",
  },
  {
    icon: "ri-user-settings-line",
    title: "Gestão completa do colaborador",
    desc: "Do onboarding ao offboarding, gerenciamos toda a jornada de benefícios do colaborador com eficiência e cuidado.",
  },
  {
    icon: "ri-focus-3-line",
    title: "Atendimento hiperpersonalizado",
    desc: "Cada empresa é única. Cada colaborador é único. Nossas soluções se adaptam ao perfil e às necessidades específicas de cada cliente.",
  },
];

export default function Diferenciais() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".dif-card").forEach((card, i) => {
              setTimeout(() => card.classList.add("dif-visible"), i * 70);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="diferenciais" className="py-16 lg:py-20 bg-[#F8F8F6]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-black" />
              <span className="text-black text-xs font-semibold tracking-widest uppercase">
                Tecnologia e Saúde Preditiva
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black leading-tight">
              Diferenciais que aceleram resultados
            </h2>
          </div>
          <p className="text-black/70 text-base leading-relaxed max-w-sm lg:text-right">
            Ferramentas e metodologias de vanguarda para uma gestão mais inteligente, humana e eficiente.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feat, idx) => (
            <div
              key={feat.title}
              className={`dif-card opacity-0 translate-y-6 transition-all duration-500 group rounded-2xl p-6 cursor-default border ${
                idx === 0
                  ? "bg-[#0D0D0D] border-[#FBC52D]/20 lg:col-span-2"
                  : "border-transparent hover:border-black/10 bg-[#FBC52D] hover:bg-[#e5b228]"
              }`}
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-5 transition-colors ${
                idx === 0 ? "bg-[#FBC52D]/15" : "bg-black/10 group-hover:bg-black/15"
              }`}>
                <i className={`${feat.icon} text-xl ${idx === 0 ? "text-[#FBC52D]" : "text-black"}`} />
              </div>
              <h3 className={`font-bold text-base mb-2 ${idx === 0 ? "text-white" : "text-black"}`}>
                {feat.title}
              </h3>
              <p className={`text-sm leading-relaxed ${idx === 0 ? "text-gray-400" : "text-black/70"}`}>
                {feat.desc}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Strip */}
        <div className="dif-card opacity-0 translate-y-6 transition-all duration-500 mt-12 flex flex-col md:flex-row items-center justify-between gap-6 bg-[#0D0D0D] rounded-2xl px-8 py-7 border border-[#FBC52D]/15">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#FBC52D]/15 shrink-0">
              <i className="ri-rocket-2-line text-[#FBC52D] text-xl" />
            </div>
            <div>
              <p className="text-white font-bold text-lg">Pronto para acelerar os resultados da sua empresa?</p>
              <p className="text-gray-400 text-sm">Converse com um especialista e descubra como a Ferrarezi fortalece pessoas e acelera resultados.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => document.querySelector("#contato")?.scrollIntoView({ behavior: "smooth" })}
              className="whitespace-nowrap bg-[#FBC52D] text-black font-bold px-7 py-3 rounded-full hover:bg-[#d4a800] transition-colors text-sm cursor-pointer"
            >
              Fale com nossos especialistas
            </button>
            <button
              onClick={() => document.querySelector("#hub-seguros")?.scrollIntoView({ behavior: "smooth" })}
              className="whitespace-nowrap border border-white/20 text-white font-semibold px-7 py-3 rounded-full hover:bg-white/8 transition-colors text-sm cursor-pointer"
            >
              Ver nossos seguros
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .dif-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}